import { z } from 'zod';
/**
 * Integration Zod schemas for API validation
 *
 * Used by the backend for request validation and by the frontend for type inference.
 */
// ── Request schemas ───────────────────────────────────────────────────────────
export const createIntegrationCredentialSchema = z.object({
    definitionUuid: z.string().uuid('definitionUuid must be a valid UUID'),
    displayName: z
        .string()
        .min(1, 'displayName is required')
        .max(100, 'displayName must be 100 characters or less')
        .trim(),
    description: z.string().max(500).trim().optional(),
    /** Plain-text credential values keyed by credential_schema field name */
    credentials: z.record(z.string(), z.string()),
    /** Non-secret config values keyed by config_schema field name */
    config: z.record(z.string(), z.string()),
    /** Optional expiry date - ISO 8601 date string (e.g. "2026-01-31") */
    credentialExpiresAt: z.string().datetime({ offset: true }).optional().nullable(),
});
export const updateIntegrationCredentialSchema = z.object({
    displayName: z
        .string()
        .min(1, 'displayName is required')
        .max(100, 'displayName must be 100 characters or less')
        .trim()
        .optional(),
    description: z.string().max(500).trim().optional().nullable(),
    config: z.record(z.string(), z.string()).optional(),
});
export const rotateIntegrationCredentialSchema = z.object({
    credentials: z.record(z.string(), z.string()),
    /** Optional updated expiry date - ISO 8601 date string */
    credentialExpiresAt: z.string().datetime({ offset: true }).optional().nullable(),
});
export const updateIntegrationStatusSchema = z.object({
    lifecycleStatus: z.enum(['active', 'disabled', 'archived']),
});
// ── Project integration schemas ───────────────────────────────────────────────
export const attachProjectIntegrationSchema = z.object({
    integrationCredentialUuid: z.string().uuid('integrationCredentialUuid must be a valid UUID'),
});
export const updateProjectIntegrationConfigSchema = z.object({
    /** Full replacement map of project-level config key-value pairs */
    config: z.record(z.string(), z.string()),
    /** Optionally enable or disable the binding */
    enabled: z.boolean().optional(),
});
// ── Repository ingestion schemas ──────────────────────────────────────────────
const repoResourceInputSchema = z.object({
    providerResourceId: z.string().min(1, 'providerResourceId is required'),
    providerResourceName: z.string().min(1, 'providerResourceName is required'),
    parentProviderResourceId: z.string().optional(),
    selectedBranch: z.string().min(1, 'selectedBranch is required'),
});
export const updateRepoResourcesSchema = z.object({
    repos: z.array(repoResourceInputSchema),
});
// ── Partial config schemas (integration, board, pipeline pages) ───────────────
/**
 * Used by the integration page to set only provider_project_id + enabled.
 * Performs a merge update - does not affect board_id or pipeline_id keys.
 */
export const updateProjectIntegrationProjectConfigSchema = z.object({
    provider_project_id: z.string(),
    /** Human-readable name for the selected provider project - stored alongside the ID for display */
    provider_project_name: z.string().optional(),
    enabled: z.boolean().optional(),
});
/**
 * Used by the Boards page to set board_id for a project integration binding.
 * Performs a merge update - does not affect provider_project_id or pipeline_id.
 */
export const updateProjectIntegrationBoardConfigSchema = z.object({
    board_id: z.string(),
});
/**
 * Used by the Pipelines page to set pipeline_id for a project integration binding.
 * Performs a merge update - does not affect provider_project_id or board_id.
 */
export const updateProjectIntegrationPipelineConfigSchema = z.object({
    pipeline_id: z.string(),
});
// ── Databricks data catalog schemas ───────────────────────────────────────────
export const updateDatabricksCatalogConfigSchema = z.object({
    catalog_name: z.string().min(1, 'catalog_name is required').trim(),
    schema_name: z.string().trim().optional().nullable(),
    allowed_tables: z.string().trim().optional().nullable(),
    enabled: z.boolean().optional(),
});
// ── AI column description schemas ─────────────────────────────────────────────
export const generateColumnDescriptionsRequestSchema = z.object({
    catalog: z.string().min(1, 'catalog is required').trim(),
    schema: z.string().min(1, 'schema is required').trim(),
    table: z.string().min(1, 'table is required').trim(),
});
//# sourceMappingURL=integration.js.map